import React, { useEffect, useMemo, useState } from "react";
import { FaRegEdit, FaSearch, FaTimes } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import { CircularProgress } from "@mui/material";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  clearActioncardsSetToast,
  fetchActioncardsBusinessUnits,
  fetchActioncardsLocationsUnits,
  fetchActioncardsSets,
  updateActioncardsSetBusinessUnits,
} from "../store/reducers/actioncardsSetSlice";
import { setActioncardsSetEditModal } from "../store/reducers/modalSlice";
import ActioncardsSetEditModal from "./actioncards-set/components/ActioncardsSetEditModal";

const itemsPerPage = 10;

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  menu: (base) => ({ ...base, zIndex: 1200 }),
};

const getActioncardSetNumber = (item) => item?.actioncard_set_number || "";

const range = (start, stop) =>
  Array.from({ length: stop - start + 1 }, (_, index) => start + index);

const uniqueValues = (values) =>
  Array.from(
    new Set(
      values.filter(
        (value) => value !== undefined && value !== null && value !== ""
      )
    )
  );

const expandActioncardSets = (rows = []) => {
  const expandedRows = [];

  rows.forEach((item) => {
    const setNumber = `${getActioncardSetNumber(item) || ""}`.trim();

    if (setNumber.includes("-")) {
      const [start, stop] = setNumber
        .split("-")
        .map((value) => parseInt(value, 10));

      if (!Number.isNaN(start) && !Number.isNaN(stop)) {
        range(start, stop).forEach((cardNumber) => {
          expandedRows.push({
            ...item,
            original_actioncard_set_number: setNumber,
            actioncard_set_number: `${cardNumber}`,
          });
        });
        return;
      }
    }

    expandedRows.push({
      ...item,
      original_actioncard_set_number: setNumber,
      actioncard_set_number: setNumber,
    });
  });

  return expandedRows.map((item, index) => ({
    ...item,
    rowKey: `${item.id || index}-${getActioncardSetNumber(item)}-${index}`,
  }));
};

const ActioncardsSet = () => {
  const dispatch = useDispatch();
  const organizationId = localStorage.getItem("organisation_id");
  const {
    items,
    businessUnitOptions,
    locationOptions,
    loading,
    optionsLoading,
    saving,
    error,
    successMessage,
  } = useSelector((state) => state.actioncardsSet);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedBusinessUnitFilter, setSelectedBusinessUnitFilter] =
    useState(null);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingItem, setEditingItem] = useState(null);
  const { actioncardsSetEditModal } = useSelector((state) => state.modal);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!organizationId) return undefined;

    const listRequest = dispatch(fetchActioncardsSets(organizationId));
    const optionsRequest = dispatch(
      fetchActioncardsBusinessUnits(organizationId)
    );
    const locationOptionsRequest = dispatch(
      fetchActioncardsLocationsUnits(organizationId)
    );

    return () => {
      listRequest.abort();
      optionsRequest.abort();
      locationOptionsRequest.abort();
    };
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearActioncardsSetToast());
    }
  }, [dispatch, successMessage]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearActioncardsSetToast());
    }
  }, [dispatch, error]);

  const expandedItems = useMemo(() => expandActioncardSets(items), [items]);

  const businessUnitNameById = useMemo(
    () =>
      businessUnitOptions.reduce((namesById, option) => {
        namesById[option.value] = option.name || option.label;
        return namesById;
      }, {}),
    [businessUnitOptions]
  );

  const locationNameById = useMemo(
    () =>
      locationOptions.reduce((locationsById, option) => {
        locationsById[option.value] = option.name || option.label;
        return locationsById;
      }, {}),
    [locationOptions]
  );

  const filteredItems = useMemo(() => {
    const searchValue = debouncedSearch.trim().toLowerCase();
    const selectedBusinessUnitId = selectedBusinessUnitFilter?.value;
    const selectedLocationId = selectedLocationFilter?.value;

    return expandedItems.filter((item) => {
      if (
        selectedBusinessUnitId &&
        !(item.business_units || []).some(
          (id) => String(id) === String(selectedBusinessUnitId)
        )
      ) {
        return false;
      }

      if (
        selectedLocationId &&
        !(item.locations_units || []).some(
          (id) => String(id) === String(selectedLocationId)
        )
      ) {
        return false;
      }

      if (!searchValue) return true;

      return [
        item?.id,
        getActioncardSetNumber(item),
        ...(item.business_units || []).map(
          (id) => `B${id}-${businessUnitNameById[id] || id}`
        ),
        ...(item.locations_units || []).map(
          (id) => `L${id}-${locationNameById[id] || id}`
        ),
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchValue);
    });
  }, [
    businessUnitNameById,
    debouncedSearch,
    expandedItems,
    locationNameById,
    selectedBusinessUnitFilter,
    selectedLocationFilter,
  ]);

  const pageCount = Math.ceil(filteredItems.length / itemsPerPage) || 1;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const getBusinessUnitLabels = (item) => {
    const ids = item.business_units || [];

    if (!ids.length) return [];

    return uniqueValues(
      ids.map((id) => [`B${id}`, businessUnitNameById[id] || id].join(" - "))
    );
  };

  const getBusinessUnitLocationLabels = (item) => {
    const ids = item.locations_units || [];

    return uniqueValues(
      ids.map((id) => [`L${id}`, locationNameById[id] || id].join(" - "))
    );
  };

  const renderChips = (values) => {
    if (!values.length) {
      return <div className="flex justify-center">-</div>;
    }

    return (
      <div className="flex flex-wrap justify-center gap-1.5">
        {values.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="inline-flex items-center rounded-md border border-admin-border bg-white px-2 py-1 text-xs font-medium text-admin-text shadow-sm"
          >
            {value}
          </span>
        ))}
      </div>
    );
  };

  const openEditModal = (item) => {
    if (!item) return;

    setEditingItem(item);
    dispatch(setActioncardsSetEditModal(true));
  };

  const closeEditModal = () => {
    if (saving) return;

    setEditingItem(null);
    dispatch(setActioncardsSetEditModal(false));
  };

  const handleModalSave = async (formData) => {
    const payload = {
      organisation_id: Number(organizationId),
      ...formData,
    };

    try {
      await dispatch(updateActioncardsSetBusinessUnits(payload)).unwrap();
      closeEditModal();
      dispatch(fetchActioncardsSets(organizationId));
    } catch (updateError) {
      // Error toast is handled from Redux state.
    }
  };

  return (
    <>
      <section className="mb-6 rounded-2xl border border-admin-border bg-white p-4 shadow-admin md:p-6">
        <div className="mb-[18px] flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="m-0 text-[22px] font-extrabold text-admin-text">
              Actioncards Set
            </h2>
            <p className="mt-1 mb-0 text-sm text-admin-muted">
              Showing {paginatedItems.length} of {filteredItems.length}{" "}
              actioncards set
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
            <div className="w-full md:w-[280px]">
              <Select
                options={businessUnitOptions}
                value={selectedBusinessUnitFilter}
                onChange={(value) => {
                  setSelectedBusinessUnitFilter(value);
                  setCurrentPage(1);
                }}
                isClearable
                isDisabled={optionsLoading}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : undefined
                }
                menuPosition="fixed"
                styles={selectMenuStyles}
                placeholder="Business unit"
              />
            </div>

            <div className="w-full md:w-[260px]">
              <Select
                options={locationOptions}
                value={selectedLocationFilter}
                onChange={(value) => {
                  setSelectedLocationFilter(value);
                  setCurrentPage(1);
                }}
                isClearable
                isDisabled={optionsLoading}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : undefined
                }
                menuPosition="fixed"
                styles={selectMenuStyles}
                placeholder="Location"
              />
            </div>

            <div className="flex min-h-[44px] w-full items-center gap-2.5 rounded-[10px] border border-gray-300 bg-white px-3 shadow-sm transition-colors focus-within:border-admin-primary md:w-[360px]">
              <FaSearch className="text-gray-400" />

              <input
                type="text"
                placeholder="Search actioncards set"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent outline-none"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="border-0 bg-transparent text-gray-400 hover:text-admin-primary"
                  aria-label="Clear actioncards set search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-admin-border shadow-sm">
          <div className="overflow-auto">
            <table className="admin-data-table m-0 w-full min-w-[860px]">
              <thead>
                <tr>
                  <th className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tl-xl bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                    ID
                  </th>
                  <th className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                    Business Unit
                  </th>
                  <th className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                    Location
                  </th>
                  <th className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                    Actioncard Set Number
                  </th>
                  <th className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tr-xl bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                    Edit
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-center text-admin-muted"
                    >
                      <CircularProgress size={14} color="inherit" />
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item, index) => (
                    <tr
                      key={item.rowKey}
                      className="transition-colors duration-200 even:bg-[#fafafa] hover:bg-[#fff3f4]"
                    >
                      <th className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-center text-[13px] font-normal text-admin-text align-middle">
                        {item.id ||
                          index + 1 + itemsPerPage * (currentPage - 1)}
                      </th>
                      <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-center text-[13px] text-admin-text align-middle">
                        {renderChips(getBusinessUnitLabels(item))}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-center text-[13px] text-admin-text align-middle">
                        {renderChips(getBusinessUnitLocationLabels(item))}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-center text-[13px] text-admin-text align-middle">
                        {getActioncardSetNumber(item)}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-2 text-center text-[13px] text-admin-text align-middle">
                        <button
                          type="button"
                          className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-primary transition-all duration-200 hover:-translate-y-px hover:text-admin-primary-hover"
                          aria-label="Edit actioncards set"
                          onClick={() => openEditModal(item)}
                        >
                          <FaRegEdit size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}

                <tr hidden={paginatedItems.length !== 0 || loading}>
                  <td
                    colSpan="5"
                    className="px-4 py-8 text-center text-admin-muted"
                  >
                    Data Not Found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {pageCount > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <ReactPaginate
              nextLabel="Next"
              previousLabel="Previous"
              onPageChange={(event) => setCurrentPage(event.selected + 1)}
              pageCount={pageCount}
              forcePage={currentPage - 1}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination flex-wrap"
              activeClassName="active"
              renderOnZeroPageCount={null}
            />
          </div>
        )}
      </section>

      <ActioncardsSetEditModal
        show={actioncardsSetEditModal}
        editingItem={editingItem}
        businessUnitOptions={businessUnitOptions}
        locationOptions={locationOptions}
        optionsLoading={optionsLoading}
        saving={saving}
        onClose={closeEditModal}
        onSave={handleModalSave}
      />
    </>
  );
};

export default ActioncardsSet;
